import { type FC, type ReactNode, useState } from 'react';

import EditCreateModal from '@zen/Components/EditCreateModal';
import type { CreateEditModalConfiguration } from '@zen/Components/EditCreateModal/types';
import { Button, Table } from '@zen/DesignSystem';
import type { SortInput } from '@zen/types';
import { useNotification } from '@zen/utils/hooks/useNotification';
import useTracking from '@zen/utils/hooks/useTracking';
import { performMutation } from '@zen/utils/performMutation';

import { ProductCategoriesTrackingAction, ProductCategoriesTrackingCategory } from '../types';
import CreateProductCategory from './CreateProductCategory';
import EditProductCategory from './EditProductCategory';
import { useDeleteProductCategoryMutation } from './graphql';
import useProductCategories from './hooks/useProductCategories';
import { getColumns } from './productCategories.tableConfiguration';
import type { CoreProductCategory } from './types';

interface Props {
  accountId: string;
  canCreateProductCategory: boolean;
}

const ProductCategories: FC<Props> = ({ accountId, canCreateProductCategory }) => {
  const [order, setOrder] = useState<SortInput | undefined>();
  const { loading, error, nodes, paginationInfo, totalCount } = useProductCategories(accountId, order);
  const { addError, addSuccess } = useNotification();
  const { trackEvent } = useTracking();
  const [deleteProductCategory] = useDeleteProductCategoryMutation();
  const [modalConfiguration, setModalConfiguration] = useState<CreateEditModalConfiguration<CoreProductCategory>>();

  const handleAdd = (): void => {
    setModalConfiguration({
      type: 'create',
      title: 'Create category',
      modalOverflowY: 'auto'
    });
  };

  const handleEdit = (category: CoreProductCategory): void => {
    setModalConfiguration({
      type: 'edit',
      initialValues: category,
      title: 'Edit category',
      modalOverflowY: 'auto'
    });
  };

  const trackCategoryDeletion = (id: string): void => {
    trackEvent({
      category: ProductCategoriesTrackingCategory,
      action: ProductCategoriesTrackingAction.CATEGORY_DELETED,
      label: id,
      properties: { categoryId: id }
    });
  };

  const handleDelete = async (id: string): Promise<void> => {
    trackCategoryDeletion(id);

    await performMutation({
      mutationFn: () =>
        deleteProductCategory({
          variables: {
            input: {
              id
            }
          },
          refetchQueries: ['productCategories'],
          awaitRefetchQueries: true
        }),
      onError: () => addError(),
      onSuccess: () => {
        addSuccess('Product category has been deleted.');
      }
    });
  };

  const handleCloseModal = (): void => setModalConfiguration(undefined);

  const actions: ReactNode = canCreateProductCategory ? (
    <Button onClick={handleAdd} size="compact">
      Create category
    </Button>
  ) : undefined;

  return (
    <>
      <Table
        actions={actions}
        columns={getColumns({ onEdit: handleEdit, onDelete: handleDelete })}
        data={nodes}
        error={!!error}
        loading={loading}
        onOrderChange={setOrder}
        order={order}
        paginationInfo={paginationInfo}
        tableId="product-categories"
        title="Categories"
        totalCountConfig={{
          entityName: 'category',
          isLoading: loading,
          totalCount
        }}
      />
      <EditCreateModal
        additionalCreateFormProps={{ accountId }}
        configuration={modalConfiguration}
        createForm={CreateProductCategory}
        editForm={EditProductCategory}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default ProductCategories;
